import React from 'react';
import { useDrop } from 'react-dnd';
import { shallowEqual } from 'react-redux';
import PropertyBase, { IBasePropertyProps } from './PropertyBase';
import Button from '../layout/Button';
import AssetUtility from '../../classes/AssetUtility';
import ModalManager from '../layout/ModalManager';
import Utils from '../../classes/Utilities';
import { AssetTypes, DndItemTypes } from '../../globals';
import { ClearIcon, PingIcon, TriangleIconRight } from '../Iconset';
import Spacer from '../layout/Spacer';
import MaterialAsset from '../../classes/MaterialAsset';
import AssetsManager from '../../classes/AssetsManager';
import { TooltipKeys } from '../tooltips/TooltipKeys';
import AssetsLibraryUtilities from '../filebrowser/AssetsLibraryUtilities';
import TextUtils from '../../classes/utilities/TextUtils';
import { useTypedSelector } from '../../state/hooks';
import AnalyticsManager from '../../classes/AnalyticsManager';
import { isIterable } from '../../classes/utilities/Misc';
import AssetPicker from '../filebrowser/AssetPicker2';

interface IAssetPropertyProps extends IBasePropertyProps
{
	/** what type(s) of assets can be selected by this property; excepts an array or a single value of AssetTypes */
	type: AssetTypes | AssetTypes[];
	/** a set of asset guids to hide from the list of selectable assets */
	filtered?: Set<string>;
	/** when set to true, the button that sets the value of the property to the currently selected asset in the asset library will not display */
	hideSetButton?: boolean;
	/** when set to true, the button that selects the assigned asset in the asset library will not display */
	hideHighlightButton?: boolean;
	/** when set to true, the button that clears the value of the property will not display */
	hideClearButton?: boolean;
	/** when set to true, no additional buttons will display */
	hideButtons?: boolean;
	/** only display assets with a specified file extension; an asset type can have multiple extensions associated with it, so this is used for finer control */
	extensionFilter?: string;
	/** when set to true, revisions of an asset will show in the list in addition to the source asset */
	showRevisions?: boolean;
	/** when set to true, assets that are not in the asset library will not show in the list */
	libraryOnly?: boolean;
	/** additional styling to the main picker button */
	buttonStyle?: React.CSSProperties;
	/** override the class name of the property */
	overrideClassName?: string;
}

export default function AssetProperty(props: IAssetPropertyProps)
{
	const validTypes = new Array<AssetTypes>().concat(props.type);
	const faceValue = generateFaceValue();
	const selectedAssets = useTypedSelector(state => state.assetModule.selectedAssets, shallowEqual);
	const dataName = (props["data-name"] ? props["data-name"] + "-" : "") + "-asset-property";
	
	if(validTypes.includes(AssetTypes.Model)) 
	{
		validTypes.push(AssetTypes.ModelProcessed);
	}

	function generateFaceValue()
	{
		if(props.multivalues)
			return "(Multiple Values)";

		if(props.value)
		{
			const assetName = AssetUtility.getAssetName(props.value);
			return assetName ? assetName : props.value;
		}

		return "Select " + [...validTypes].map((typeName) => {return TextUtils.prettifyText(typeName)}).join(", ").replace("Slide", "UI Template").replace("Visage", "UI");
	}

	const [{ canDrop }, drop] = useDrop({
		accept: [
			DndItemTypes.ASSET_TREE_ITEM,
			DndItemTypes.ASSET_PREVIEW_ITEM
		],
		collect: monitor => ({
			hovered: monitor.isOver({ shallow: true }),
			canDrop: monitor.canDrop(),
		}),
		canDrop: (item: any, monitor) =>
		{
			if (!item || !isIterable(item.selected))
			{
				return false;
			}

			for (const asset of item.selected)
			{
				if (!validTypes.includes(asset.props.type)) return false;
				if (props.extensionFilter && Utils.getFileExtension(asset.props.label) !== props.extensionFilter) return false;
			}

			return true;
		},
		drop: (item: any, monitor) =>
		{
			if (!monitor.didDrop())
				onAssetChosen(item.selected[0].props.nodeId);
		},
	});

	function onAssetChosen(assetId: string)
	{
		// update used assets
		const assetProps = AssetsManager.getPropsForAsset(assetId);
		if(assetProps && assetProps.type !== AssetTypes.Material) // special case; materials are handled differently
		{
			AssetsManager.removeUsedAsset(props.value);
			if (assetId)
			{
				if(assetId !== props.value)
				{
					AssetsManager.addUsedAsset(assetId);
				}
			}
		}

		// apply change
		if (props.onCommitCallback)
		{
			props.onCommitCallback(assetId);
		}
		else if (props.onChangeCallback)
		{
			props.onChangeCallback(assetId);
		}
		AssetsLibraryUtilities.refresh();
	}

	function openAssetDialog()
	{
		ModalManager.addModal({
			content:
				<AssetPicker 
					data-name={dataName + "-picker"}
					value={props.value} 
					type={props.type}
					extensionFilter={props.extensionFilter}
					filtered={props.filtered}
					showRevisions={props.showRevisions}
					libraryOnly={props.libraryOnly ? props.libraryOnly : true}
					assetSelectedCallback={(assetId) => 
					{
						AnalyticsManager.managedEvent('propertyChanged', 'propertyId', props["data-name"] ? props["data-name"] + "-asset" : "asset-property");
						onAssetChosen(assetId);
					}}
				/>,
		});
	}

	return drop(
		<div className={`assetPropertyWrapper ${props.overrideClassName}`}>
			<PropertyBase {...props}
				data-name={dataName + "-root"}
				highlight={canDrop}
				required={props.required && !props.value}
			>
				<Button data-name={dataName + "-button"}
					silent={true}
					label={faceValue}
					style={props.buttonStyle}
					disabled={props.readOnly}
					onClick={openAssetDialog}
				/>
				<Spacer size={{ x: "2px" }} />
				{
					props.hideButtons || props.hideSetButton || props.readOnly ? null :
						<TriangleIconRight rotate={180}
							cellclass={"propertySmallCell"}
							data-name={dataName + "-set-value-button"}
							onClick={() =>
							{
								if(selectedAssets?.length)
								{
									const currentlySelectedAsset = AssetsManager.getPropsForAsset(selectedAssets[0]); // TODO: handle multi-selection when we have it
									if (currentlySelectedAsset && validTypes.includes(currentlySelectedAsset.type) &&
										(!props.extensionFilter || Utils.getFileExtension(currentlySelectedAsset.label) === props.extensionFilter))
									{
										onAssetChosen(currentlySelectedAsset.nodeId);
									}
								}
							}}
							tooltip={TooltipKeys.AssetPickerSetFromLibrary}
						/>
				}
				{
					props.hideButtons || props.hideHighlightButton || props.readOnly ? null :
						<PingIcon scale={0.7}
							cellclass={"propertySmallCell"}
							data-name={dataName + "-select-value-button"}
							onClick={() =>
							{
								AssetsLibraryUtilities.selectAssets([props.value], { autoScroll: true, autoExpand: true });
							}}
							tooltip={TooltipKeys.AssetPickerSelectInLibrary}
						/>
				}
				{
					props.hideButtons || props.hideClearButton || props.readOnly ? null :
						<ClearIcon scale={0.7}
							cellclass={"propertySmallCell"}
							data-name={dataName + "-clear-value-button"}
							onClick={() => onAssetChosen(props.type === AssetTypes.Material ? MaterialAsset.defaultMaterialGuid : "")}
							tooltip={TooltipKeys.AssetPickerClear}
						/>
				}
			</PropertyBase>
		</div>
	);
}